import React, { useEffect, useState } from 'react';
import { Eye, MapPin, Globe, Heart, Star, Target, ShieldCheck, Lock, Loader2, CheckCircle2, ChevronRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { getRecruiterPreScreens, type PreScreenRecruiterResult } from '../../api/interview';

/**
 * Read-only recruiter-facing passport sections.
 *
 * These render from data the recruiter context already holds (e.g. the
 * recruiter-gated Spotlight / candidate feed) — they NEVER fetch a candidate's
 * private data themselves. Security stays in the backend: the parent only ever
 * receives these fields from recruiter-gated endpoints.
 */

export interface RecruiterPrefs {
  career_status?: string | null;
  base_locations?: string | null;
  preferred_locations?: string | null;
  role_preferences?: string | null;
  current_role?: string | null;
  current_level?: string | null;
}

const Field: React.FC<{ icon: React.ReactNode; label: string; children: React.ReactNode }> = ({ icon, label, children }) => (
  <div>
    <label className="text-[11px] sm:text-xs text-slate-500 flex items-center gap-1.5">{icon}{label}</label>
    <p className="text-xs sm:text-sm text-slate-700 mt-0.5">{children}</p>
  </div>
);

export const RecruiterStatusView: React.FC<{ prefs?: RecruiterPrefs | null }> = ({ prefs }) => {
  const { t } = useTranslation(['passport', 'profile']);
  const notSpecified = t('profile:jobPreferences.notSpecified');

  const statusKey = prefs?.career_status || 'invisible';
  const statusLabel =
    statusKey === 'actively_seeking'
      ? t('profile:careerStatus.options.actively_seeking')
      : statusKey === 'curious'
      ? t('profile:careerStatus.options.curious')
      : t('profile:careerStatus.options.invisible');

  return (
    <div className="max-w-2xl mx-auto px-3 sm:px-4 py-4 space-y-4">
      <div>
        <h3 className="text-sm sm:text-base font-semibold text-slate-800">{t('passport:prefs.title')}</h3>
      </div>

      <Field icon={<Eye className="w-3.5 h-3.5 text-pink-600" />} label={t('passport:prefs.visibilityLabel')}>
        {statusLabel}
      </Field>

      <div className="pt-3 border-t border-slate-100 grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Field icon={<MapPin className="w-3.5 h-3.5 text-pink-600" />} label={t('profile:jobPreferences.baseLocations')}>
          {prefs?.base_locations || notSpecified}
        </Field>
        <Field icon={<Globe className="w-3.5 h-3.5 text-pink-600" />} label={t('profile:jobPreferences.preferredLocations')}>
          {prefs?.preferred_locations || notSpecified}
        </Field>
        <Field icon={<Heart className="w-3.5 h-3.5 text-pink-600" />} label={t('profile:jobPreferences.rolePreferences')}>
          {prefs?.role_preferences || notSpecified}
        </Field>
        <Field icon={<Star className="w-3.5 h-3.5 text-pink-600" />} label={t('profile:jobPreferences.currentRole')}>
          {prefs?.current_role || notSpecified}
        </Field>
        <Field icon={<Target className="w-3.5 h-3.5 text-pink-600" />} label={t('profile:jobPreferences.currentLevel')}>
          {prefs?.current_level || notSpecified}
        </Field>
      </div>
    </div>
  );
};

function fmtScore(score: number | null): string {
  if (score == null) return '-';
  return `${Math.round(score)}%`;
}

/**
 * Recruiter view of a candidate's pre-screen results.
 *
 * Fetches the recruiter-gated endpoint by stable candidate id. The backend
 * (gateway) returns ONLY the results the candidate has made visible — an empty
 * list means the candidate hasn't shared any pre-screen.
 */
export const RecruiterPreScreenView: React.FC<{
  candidateId?: string | null;
  /** Preloaded recruiter-gated results (e.g. from PassportViewer) to skip a refetch. */
  results?: PreScreenRecruiterResult[] | null;
}> = ({ candidateId, results: providedResults }) => {
  const { t } = useTranslation(['passport']);
  const { apiCall } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(providedResults == null);
  const [results, setResults] = useState<PreScreenRecruiterResult[]>(providedResults ?? []);

  useEffect(() => {
    if (providedResults != null) {
      setResults(providedResults);
      setLoading(false);
      return;
    }
    let cancelled = false;
    const run = async () => {
      if (!candidateId) {
        setLoading(false);
        return;
      }
      try {
        const res = await getRecruiterPreScreens(apiCall, candidateId);
        if (!cancelled) setResults(res.prescreens || []);
      } catch {
        if (!cancelled) setResults([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    run();
    return () => {
      cancelled = true;
    };
  }, [candidateId, apiCall, providedResults]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16 text-slate-400 text-sm">
        <Loader2 className="w-4 h-4 animate-spin mr-2" /> …
      </div>
    );
  }

  if (results.length === 0) {
    return (
      <div className="max-w-2xl mx-auto px-3 sm:px-4 py-8">
        <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-8 text-center">
          <div className="mx-auto w-12 h-12 rounded-full bg-white border border-slate-200 flex items-center justify-center mb-3">
            <Lock className="w-6 h-6 text-slate-400" />
          </div>
          <h3 className="text-sm sm:text-base font-semibold text-slate-800 mb-1">{t('passport:prescreen.title')}</h3>
          <p className="text-[11px] sm:text-xs text-slate-500 max-w-sm mx-auto">
            {t('passport:prescreen.recruiterEmpty', 'This candidate has not shared an Intro result.')}
          </p>
        </div>
      </div>
    );
  }

  // Group all shared attempts by family (results arrive newest-first).
  const families: { family_key: string; title: string; attempts: PreScreenRecruiterResult[] }[] = [];
  const byFamily = new Map<string, number>();
  for (const r of results) {
    let idx = byFamily.get(r.family_key);
    if (idx == null) {
      idx = families.length;
      byFamily.set(r.family_key, idx);
      families.push({
        family_key: r.family_key,
        title: r.template_title || t('passport:prescreen.title'),
        attempts: [],
      });
    }
    families[idx].attempts.push(r);
  }

  return (
    <div className="max-w-2xl mx-auto px-3 sm:px-4 py-4 space-y-3">
      <div>
        <h3 className="text-sm sm:text-base font-semibold text-slate-800">{t('passport:prescreen.title')}</h3>
        <p className="text-[11px] sm:text-xs text-slate-500 mt-0.5">
          {t('passport:prescreen.recruiterSubtitle', 'Standardised Cevee Intros the candidate chose to share.')}
        </p>
      </div>
      {families.map((fam) => (
        <div key={fam.family_key} className="rounded-2xl border border-slate-200 bg-white p-4">
          <div className="flex items-center gap-2.5 min-w-0">
            <div className="w-9 h-9 rounded-full bg-pink-100 flex items-center justify-center flex-shrink-0">
              <ShieldCheck className="w-4.5 h-4.5 text-pink-500" />
            </div>
            <div className="min-w-0">
              <p className="text-xs sm:text-sm font-semibold text-slate-800 truncate">{fam.title}</p>
              <p className="text-[11px] text-slate-500 mt-0.5">
                {t('passport:prescreen.attemptsShared', 'Shared attempts: {{count}}', { count: fam.attempts.length })}
              </p>
            </div>
          </div>
          <div className="mt-3 space-y-1.5">
            {fam.attempts.map((r, i) => {
              const clickable = !!candidateId;
              return (
                <div
                  key={r.session_id}
                  role={clickable ? 'button' : undefined}
                  tabIndex={clickable ? 0 : undefined}
                  onClick={clickable ? () => navigate(`/recruiter/prescreen/${candidateId}/${r.session_id}`) : undefined}
                  onKeyDown={clickable ? (e) => { if (e.key === 'Enter') navigate(`/recruiter/prescreen/${candidateId}/${r.session_id}`); } : undefined}
                  title={clickable ? t('passport:prescreen.viewDetails', 'View details') : undefined}
                  className={`flex items-center justify-between gap-3 rounded-xl border border-slate-100 bg-slate-50 px-3 py-2 ${
                    clickable ? 'cursor-pointer hover:border-pink-200 hover:bg-pink-50/40 transition-colors' : ''
                  }`}
                >
                  <div className="flex items-center gap-2 min-w-0">
                    <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500 flex-shrink-0" />
                    <span className="text-[11px] sm:text-xs text-slate-600 truncate">
                      {r.completed_at ? new Date(r.completed_at * 1000).toLocaleDateString() : ''}
                    </span>
                    {i === 0 && (
                      <span className="text-[9px] font-semibold px-1.5 py-0.5 rounded-full bg-pink-100 text-pink-600 flex-shrink-0">
                        {t('passport:prescreen.latestBadge', 'Latest')}
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    {/* Score hidden from UI */}
                    {clickable && <ChevronRight className="w-3.5 h-3.5 text-slate-300" />}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
};
